import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Tag, ArrowLeft } from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import DOMPurify from 'dompurify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsDetailPage() {
  const { theme } = useTheme();
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchArticle = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/news/${id}`);
      setArticle(response.data);
    } catch (err) {
      setError('Article not found');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchArticle();
  }, [fetchArticle]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div data-testid="news-detail-page" className={`min-h-screen pt-24 flex items-center justify-center ${
        theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'
      }`}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#00C8EB] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div data-testid="news-detail-page" className={`min-h-screen pt-24 flex items-center justify-center ${
        theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'
      }`}>
        <div className="text-center">
          <h1 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Article Not Found
          </h1>
          <Link to="/news">
            <Button className="bg-[#00C8EB] text-[#0A0A10]">
              <ArrowLeft size={16} className="mr-2" />
              Back to News
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="news-detail-page" className={`min-h-screen pt-24 ${theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'}`}>
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Back link */}
          <Link 
            to="/news" 
            className={`inline-flex items-center gap-2 mb-8 hover:text-[#00C8EB] transition-colors ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            <ArrowLeft size={16} />
            Back to News
          </Link>

          {/* Meta */}
          <div className="flex items-center gap-4 mb-4">
            <span className="inline-flex items-center gap-1 text-sm font-medium text-[#00C8EB] bg-[#00C8EB]/10 px-3 py-1 rounded-full">
              <Tag size={14} weight="duotone" />
              {article.category}
            </span>
            <span className={`flex items-center gap-1 text-sm ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
            }`}>
              <Calendar size={14} weight="duotone" />
              {formatDate(article.created_at)}
            </span>
          </div>

          {/* Title */}
          <h1 className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-6 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            {article.title}
          </h1>

          {/* Summary */}
          <p className={`text-xl mb-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {article.summary}
          </p>

          {/* Image */}
          {article.image_url && (
            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={article.image_url}
                alt={article.title}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Content */}
          <div 
            className={`prose prose-lg max-w-none ${
              theme === 'dark' ? 'prose-invert' : ''
            }`}
            style={{
              '--tw-prose-body': theme === 'dark' ? '#d1d5db' : '#4b5563',
              '--tw-prose-headings': theme === 'dark' ? '#ffffff' : '#111827',
              '--tw-prose-links': '#00C8EB',
            }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
          />

          {/* CTA */}
          <div className={`mt-12 p-8 rounded-2xl text-center ${
            theme === 'dark' ? 'bg-[#12131F] border border-[#464B8B]/20' : 'bg-gray-50'
          }`}>
            <h3 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Interested in learning more?
            </h3>
            <p className={`mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Get in touch with our team to discuss how we can help your business.
            </p>
            <Link to="/contact">
              <Button className="bg-[#00C8EB] hover:bg-[#00C8EB]/90 text-[#0A0A10]">
                Contact Us
              </Button>
            </Link>
          </div>
        </motion.div>
      </article>
    </div>
  );
}
